import { Value } from "@/layouts";
import { Redeems } from "@/models";
import { formatDate, getColorBadgeStatus, getStatus } from "@/utils";
import { Flex, Text } from "@chakra-ui/react";
import { Pills, Icon } from "barte-design-system";
import { ArrowCircleDown, ArrowCircleUp, Wallet } from "@phosphor-icons/react";
import { DynamicTable, ColumnDefinition } from "@/components/DynamicTable";

export interface RedeemHistoryProps {
  redeems: Redeems[];
  isLoading?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  onPageChange?: (page: number) => void;
}

function RedeemHistory({
  redeems,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageSizeChange,
  onPageChange,
}: RedeemHistoryProps) {
  const flattenedData = redeems?.flatMap((redeem, index) => {
    const mainRow = { ...redeem, isMainRow: true, originalIndex: index };
    if (redeem.subSellers?.length > 0) {
      const subRows = redeem.subSellers.map((subSeller: any) => ({
        ...subSeller,
        isMainRow: false,
        originalIndex: index,
        parentUuidCharge: redeem.uuidCharge,
      }));
      return [mainRow, ...subRows];
    }
    return [mainRow];
  }) || [];

  const columns: ColumnDefinition<Redeems & { isMainRow?: boolean; originalIndex?: number; parentUuidCharge?: string }>[] = [
    {
      key: "executionDate",
      title: "Execução",
      tooltipMessage: "A data da operação é quando o pagamento é compensado",
      dataIndex: "executionDate",
      render: (executionDate: string, record: any) => (
        <Flex alignItems="center" gap="2">
          {(record.creditOperation && record.statementType !== "DEBIT_TRANSFER") && (
            <Icon
              icon={ArrowCircleDown}
              size={20}
              weight="regular"
              color={record.isRefund ? "var(--content-tertiary)" : "var(--accent-green)"}
            />
          )}
          {(!record.creditOperation && record.statementType !== "DEBIT_TRANSFER") && (
            <Icon icon={ArrowCircleUp} size={20} weight="regular" color="var(--accent-red)" />
          )}
          {record.statementType === "DEBIT_TRANSFER" && (
            <Icon icon={Wallet} size={20} weight="regular" color="var(--content-primary)" />
          )}
          <Text fontSize="sm" color="var(--content-primary)">{formatDate(executionDate)}</Text>
        </Flex>
      ),
    },
    {
      key: "operation",
      title: "Operação",
      dataIndex: "operation",
      render: (operation: string, record: any) => {
        let pillToShow = null;

        if (record.chargeStatus === "REFUND") {
          pillToShow = <Pills label={getStatus("REFUND")} variant="light" state={getColorBadgeStatus("CHARGEBACK_LIST")} size="sm" />;
        } else if (record.chargeStatus === "CHARGEBACK") {
          pillToShow = <Pills label={getStatus("CHARGEBACK")} variant="light" state={getColorBadgeStatus("CHARGEBACK_LIST")} size="sm" />;
        } else if (record.chargeStatus === "DISPUTE") {
          pillToShow = <Pills label={getStatus("DISPUTE")} variant="light" state={getColorBadgeStatus("DISPUTE")} size="sm" />;
        } else if (record.statementType?.includes("CREDIT_ANTICIPATED")) {
          pillToShow = <Pills label={getStatus("CREDIT_ANTICIPATED")} variant="light" state={getColorBadgeStatus("CREDIT_ANTICIPATED")} size="sm" />;
        } else if (record.statementType?.includes("CREDIT_REVERSAL")) {
          pillToShow = <Pills label={getStatus("CREDIT_REVERSAL")} variant="light" state={getColorBadgeStatus("CHARGEBACK_LIST")} size="sm" />;
        } else if (record.isRefund) {
          pillToShow = <Pills label={getStatus("REFUND")} variant="light" state={getColorBadgeStatus("CHARGEBACK_LIST")} size="sm" />;
        } else if (record.isChargeback) {
          pillToShow = <Pills label={getStatus("CHARGEBACK")} variant="light" state={getColorBadgeStatus("CHARGEBACK_LIST")} size="sm" />;
        }

        return (
          <Flex direction="column" gap="1">
            <Flex gap="2" align="center">
              {pillToShow}
              <Text fontSize="sm" color="var(--content-primary)" noOfLines={1}>
                {record.name ? `${operation} - Split - ${record.name}` : operation}
              </Text>
            </Flex>
            {(record.buyer) && (
              <Text fontSize="xs" noOfLines={1} color="var(--content-tertiary)">{record.buyer}</Text>
            )}
          </Flex>
        );
      },
    },
    {
      key: "providerType",
      title: "Destino",
      dataIndex: "providerType",
      render: (providerType: string, record: any) => (
        <Text fontSize="sm" color="var(--content-primary)">
          {record.statementType?.includes("DEBIT_TRANSFER") ? "" : (providerType ?? "-")}
        </Text>
      ),
    },
    {
      key: "tax",
      title: "Taxas",
      dataIndex: "tax",
      render: (tax: any, record: any) =>
        tax ? (
          <Value
            isChargeback={record.isChargeback}
            isRefund={record.isRefund}
            amount={tax?.amount}
            creditOperation={tax?.creditOperation}
            isTax
          />
        ) : null,
    },
    {
      key: "amount",
      title: "Valor",
      dataIndex: "amount",
      render: (amount: number, record: any) => (
        <Value
          isChargeback={record.isChargeback}
          isRefund={record.isRefund}
          amount={amount}
          creditOperation={record.creditOperation}
          statementType={record.statementType}
        />
      ),
    },
  ];

  return (
    <DynamicTable
      data={flattenedData}
      columns={columns}
      loading={isLoading}
      rowKey={(record) => `${record.originalIndex || 0}-${record.isMainRow ? 'main' : 'sub'}-${record.executionDate || ''}`}
      onRowClick={(record) => {
        const uuidCharge = record.uuidCharge || record.parentUuidCharge;
        if (uuidCharge) {
          window.open(`/cobrancas/${uuidCharge}`, '_blank');
        }
      }}
      onRow={(record) => ({
        onClick: () => {
          const uuidCharge = record.uuidCharge || record.parentUuidCharge;
          if (uuidCharge) {
            window.open(`/cobrancas/${uuidCharge}`, '_blank');
          }
        },
        style: {
          cursor: (record.uuidCharge || record.parentUuidCharge) ? "pointer" : "default",
        },
      })}
      locale={{ emptyText: "Você não possui transações no período selecionado" }}
      pagination={
        currentPage !== undefined && totalPages !== undefined && onPageChange
          ? {
              currentPage: currentPage + 1,
              totalPages: totalPages,
              pageSize: pageSize || 10,
              onPageSizeChange: onPageSizeChange || (() => {}),
              onPageChange: onPageChange,
              totalItems: totalItems,
            }
          : undefined
      }
    />
  );
}

export { RedeemHistory };
